import i18n from 'i18next'

import type { I18nMessage, I18nText } from '@ambire-common/libs/i18n/i18nMessage'
import { i18nKey } from '@common/config/localization/constants'
import { CONTROLLER_MESSAGES } from '@common/config/localization/controllerMessages.generated'
import { HUMANIZER_WARNING_MESSAGES } from '@common/config/localization/humanizerMessages'

// ambire-common (submodule) produces English text — controller errors, toasts,
// SignAccountOp errors, banners. We don't edit it for i18n; instead the UI passes
// that text through translateControllerMessage() where it is shown. Text we
// don't recognise is returned unchanged (English), never half-translated.
//
// Two sources of keys:
// - CONTROLLER_MESSAGES: every literal/template message in ambire-common,
//   regenerated by `yarn i18n:extract` (scripts/i18n/controllerMessages.mjs).
// - The estimation/broadcast error humanizer (libs/errorHumanizer) glues
//   "<prefix> because" + "<reason clause>" + " Error code: …" + "\nPlease try
//   again…". Clauses like "of a low gas limit." can't be translated alone, so each
//   part maps to a standalone sentence below, and the translation is rebuilt as
//   "<prefix sentence> <reason sentence> …". translateControllerMessage.test.ts
//   fails when upstream adds or rewords one of these.

const ERROR_PREFIXES: Record<string, string> = {
  'Transaction cannot be sent because': i18nKey('The transaction cannot be sent.'),
  'The transaction cannot be broadcast because': i18nKey('The transaction cannot be broadcast.'),
  'Transaction cannot be simulated because': i18nKey('The transaction cannot be simulated.')
}

/** libs/errorHumanizer reason clause → standalone sentence. */
export const ERROR_REASONS: Record<string, string> = {
  // errors.ts — BROADCAST_OR_ESTIMATION_ERRORS
  'the RPC provider does not support the requested operation. Please check your RPC settings or contact the app team.':
    i18nKey(
      'The RPC provider does not support the requested operation. Please check your RPC settings or contact the app team.'
    ),
  'of a problem with the RPC on this network. Please try again later, change the RPC or contact support for assistance.':
    i18nKey(
      'There is a problem with the RPC on this network. Please try again later, change the RPC or contact support for assistance.'
    ),
  'of a RPC request that contains invalid or missing parameters. Please try again later or contact support.':
    i18nKey(
      'An RPC request contains invalid or missing parameters. Please try again later or contact support.'
    ),
  'your wallet has sent too many requests in a short time. Please wait a moment and try again.':
    i18nKey(
      'Your wallet has sent too many requests in a short time. Please wait a moment and try again.'
    ),
  'of a server error of the RPC provider. Please wait a moment or try changing the RPC provider in settings.':
    i18nKey(
      'The RPC provider returned a server error. Please wait a moment or try changing the RPC provider in settings.'
    ),
  'of a low gas limit. Please try again or contact support for assistance.': i18nKey(
    'The gas limit is too low. Please try again or contact support for assistance.'
  ),
  'of insufficient funds for the transaction fee. Please add more fee tokens to your account and try again.':
    i18nKey(
      'There are not enough funds for the transaction fee. Please add more fee tokens to your account and try again.'
    ),
  'the transfer amount exceeds your account balance. Please check your balance or adjust the transfer amount.':
    i18nKey(
      'The transfer amount exceeds your account balance. Please check your balance or adjust the transfer amount.'
    ),
  'you do not have enough of the specified NFT in your account. Please check your balance or adjust the transfer amount.':
    i18nKey(
      'You do not have enough of the specified NFT in your account. Please check your balance or adjust the transfer amount.'
    ),
  'it is underpriced. Please select a higher transaction speed and try again.': i18nKey(
    'The transaction is underpriced. Please select a higher transaction speed and try again.'
  ),
  'Insufficient ETH': i18nKey('Insufficient ETH'),
  'Insufficient AVAX': i18nKey('Insufficient AVAX'),
  'Insufficient BNB': i18nKey('Insufficient BNB'),
  'Insufficient POL': i18nKey('Insufficient POL'),
  'the gas sponsorship was refused by the app. Please try again by paying for the gas instead':
    i18nKey('The app refused to sponsor the gas. Please try again and pay for the gas yourself.'),
  "the Paymaster has insufficient funds. Please report this to the team. We've disabled it, so please try again with the updated fee payment options.":
    i18nKey(
      "The Paymaster has insufficient funds. Please report this to the team. We've disabled it, so please try again with the updated fee payment options."
    ),
  "gas sponsorship is currently unavailable on this network. Please add a small amount of the network's native token to this account and pay the fee yourself, or try again later.":
    i18nKey(
      "Gas sponsorship is currently unavailable on this network. Please add a small amount of the network's native token to this account and pay the fee yourself, or try again later."
    ),
  // Upstream ends this one with "\nPlease try again or contact ZKX support…",
  // which is split off like the generic support suffix.
  'the ZKX relayer is temporarily down.': i18nKey('The ZKX relayer is temporarily down.'),
  'of a pending transaction. Please try broadcasting again.': i18nKey(
    'There is a pending transaction. Please try broadcasting again.'
  ),
  'the input token amount is too low. Please increase the token amount and try again.': i18nKey(
    'The input token amount is too low. Please increase the token amount and try again.'
  ),
  'the slippage tolerance was exceeded.': i18nKey('The slippage tolerance was exceeded.'),
  'the slippage tolerance was exceeded. Please try changing the from amount.': i18nKey(
    'The slippage tolerance was exceeded. Please try changing the amount you send.'
  ),
  "the smart contract you're interacting with doesn't support this operation. This could be due to contract restrictions, insufficient permissions, or specific conditions that haven't been met. Please review the requirements of this operation or consult the contract documentation.":
    i18nKey(
      "The smart contract you're interacting with doesn't support this operation. This could be due to contract restrictions, insufficient permissions, or specific conditions that haven't been met. Please review the requirements of this operation or consult the contract documentation."
    ),
  'of one of the following reasons: missing approval, insufficient approved amount, the amount exceeds the account balance.':
    i18nKey(
      'Possible reasons: missing approval, insufficient approved amount, or the amount exceeds the account balance.'
    ),
  'the swap has expired. Return to the app and reinitiate the swap if you wish to proceed.':
    i18nKey(
      'The swap has expired. Return to the app and start the swap again if you wish to proceed.'
    ),
  'the token permit or signed approval has expired (Permit2 / AllowanceHolder). Open the dapp again and submit a fresh approval or quote so the signature deadline is renewed.':
    i18nKey(
      'The token permit or signed approval has expired (Permit2 / AllowanceHolder). Open the dapp again and submit a fresh approval or quote so the signature deadline is renewed.'
    ),
  'of low liquidity, slippage limits, or insufficient token approval.': i18nKey(
    'This can be caused by low liquidity, slippage limits, or insufficient token approval.'
  ),
  'of a Swap failure. Please try performing the same swap again.': i18nKey(
    'The swap failed. Please try performing the same swap again.'
  ),
  'Quote expired': i18nKey('Quote expired'),
  'Insufficient token amount': i18nKey('Insufficient token amount'),
  'the Swap path is invalid. Return to the app and reinitiate the swap if you wish to proceed.':
    i18nKey(
      'The swap path is invalid. Return to the app and start the swap again if you wish to proceed.'
    ),
  'your health factor will drop below the liquidation threshold.': i18nKey(
    'Your health factor would drop below the liquidation threshold.'
  ),
  // errors.ts — BROADCAST_ERRORS
  'the selected fee is too low. Please select a higher transaction speed and try again.': i18nKey(
    'The selected fee is too low. Please select a higher transaction speed and try again.'
  ),
  'the gas fee for replacing a pending transaction is too low. Please try again.': i18nKey(
    'The gas fee for replacing a pending transaction is too low. Please try again.'
  ),
  'the fee set for the transaction is lower than the network’s current base fee. Please try again with a higher fee.':
    i18nKey(
      'The fee set for the transaction is lower than the network’s current base fee. Please try again with a higher fee.'
    ),
  'of a network error. Please check your internet connection and broadcast the transaction again.':
    i18nKey(
      'A network error occurred. Please check your internet connection and broadcast the transaction again.'
    ),
  // errors.ts — ESTIMATION_ERRORS
  'of a network error. Please check your internet connection or contact support if the issue persists.':
    i18nKey(
      'A network error occurred. Please check your internet connection or contact support if the issue persists.'
    ),
  'your account key lacks the necessary permissions. Ensure that you have authorization to sign or use an account with sufficient privileges.':
    i18nKey(
      'Your account key lacks the necessary permissions. Ensure that you have authorization to sign or use an account with sufficient privileges.'
    ),
  'this app does not support Smart Account wallets. Use an EOA account instead.': i18nKey(
    'This app does not support Smart Account wallets. Use an EOA account instead.'
  ),
  'the NFT you are trying to mint is already minted. This can also happen if you have batched multiple mint transactions for the same NFT.':
    i18nKey(
      'The NFT you are trying to mint is already minted. This can also happen if you have batched multiple mint transactions for the same NFT.'
    ),
  'the NFT you are trying to interact with does not exist. Ensure you are using the correct token ID.':
    i18nKey(
      'The NFT you are trying to interact with does not exist. Ensure you are using the correct token ID.'
    ),
  'it reverted onchain with reason unknown.': i18nKey('It reverted onchain for an unknown reason.'),
  'your account lacks the necessary permissions to perform this action. Please contact the contract owner or ensure you have the required role.':
    i18nKey(
      'Your account lacks the necessary permissions to perform this action. Please contact the contract owner or ensure you have the required role.'
    ),
  'the contract is currently paused. Please wait until it is active or contact the contract owner for more information.':
    i18nKey(
      'The contract is currently paused. Please wait until it is active or contact the contract owner for more information.'
    ),
  'the contract’s size exceeds the EVM limit for deployment. Please ensure the contract is optimized before redeploying.':
    i18nKey(
      'The contract’s size exceeds the EVM limit for deployment. Please ensure the contract is optimized before redeploying.'
    ),
  'the smart contract’s initialization failed. This is likely a deployment issue; please check the constructor parameters.':
    i18nKey(
      'The smart contract’s initialization failed. This is likely a deployment issue; please check the constructor parameters.'
    ),
  // errors.ts — ESTIMATION_ERRORS (ZKX: libs/estimate/tokenBalanceCheck)
  'the amount is higher than your current token balance. Your balance may have just changed, for example after a DCA order was executed. Lower the amount or try again once your balance updates.':
    i18nKey(
      'The amount is higher than your current token balance. Your balance may have just changed, for example after a DCA order was executed. Lower the amount or try again once your balance updates.'
    ),
  // errors.ts — DEPLOYLESS_ERRORS
  "the network or provider don't support our Deployless contracts.": i18nKey(
    "The network or provider doesn't support our Deployless contracts."
  ),
  // helpers.ts — getGenericMessageFromType
  'of a Paymaster Error.': i18nKey('A Paymaster error occurred.'),
  "it's invalid.": i18nKey('It is invalid.'),
  'of an unknown error.': i18nKey('An unknown error occurred.'),
  'it will revert onchain.': i18nKey('It would revert onchain.'),
  'it will revert onchain with reason unknown.': i18nKey(
    'It would revert onchain for an unknown reason.'
  ),
  'of a contract error.': i18nKey('A contract error occurred.')
}

const ERROR_REASON_PATTERNS: { re: RegExp; key: string }[] = [
  {
    re: /^of an unknown error \(Origin: (?<origin>[^)]*) call\)\.$/,
    key: i18nKey('An unknown error occurred ({{origin}} call).')
  }
]

/** Whole humanized messages that have no prefix/clause structure. */
const ERROR_WHOLE_MESSAGES: Record<string, string> = {
  'Transaction cannot be sent because of an unknown error. Please try again or contact ZKX support for assistance.':
    i18nKey(
      'The transaction cannot be sent because of an unknown error. Please try again or contact ZKX support for assistance.'
    ),
  'An unknown error occurred while broadcasting the transaction. Please try again or contact ZKX support for assistance.':
    i18nKey(
      'An unknown error occurred while broadcasting the transaction. Please try again or contact ZKX support for assistance.'
    ),
  'Transaction invalid: out of gas': i18nKey('Transaction invalid: out of gas'),
  'Transaction rejected.': i18nKey('Transaction rejected.')
}

const SUPPORT_SUFFIX = '\nPlease try again or contact ZKX support for assistance.'
const SUPPORT_SENTENCE = i18nKey('Please try again or contact ZKX support for assistance.')
const ERROR_CODE_SENTENCE = i18nKey('Error code: {{code}}')
const UNEXPECTED_ISSUE = 'We encountered an unexpected issue:'
const UNEXPECTED_ISSUE_SENTENCE = i18nKey('We encountered an unexpected issue: {{reason}}')

// Every key passed here comes from an i18nKey() table (this file or the
// generated one), so it is extracted; getFixedT keeps i18n:audit from counting
// the call as a dynamic t().
const lookup = (key: string, options?: Record<string, string | number>) => {
  const out = i18n.getFixedT(i18n.language)(key, options as any) as unknown
  return typeof out === 'string' && out ? out : null
}

const translateReason = (reason: string): string | null => {
  const key = ERROR_REASONS[reason]
  if (key) return lookup(key)
  for (const { re, key: patternKey } of ERROR_REASON_PATTERNS) {
    const match = reason.match(re)
    if (match) return lookup(patternKey, match.groups)
  }
  return null
}

const translateHumanizedError = (message: string): string | null => {
  const whole = ERROR_WHOLE_MESSAGES[message]
  if (whole) return lookup(whole)

  let body = message
  let support = false
  if (body.endsWith(SUPPORT_SUFFIX)) {
    support = true
    body = body.slice(0, -SUPPORT_SUFFIX.length)
  }

  const withSupport = (text: string | null) =>
    text && support ? `${text}\n${lookup(SUPPORT_SENTENCE) ?? ''}` : text

  if (body.startsWith(UNEXPECTED_ISSUE)) {
    return withSupport(
      lookup(UNEXPECTED_ISSUE_SENTENCE, { reason: body.slice(UNEXPECTED_ISSUE.length).trim() })
    )
  }

  let code: string | null = null
  const codeIndex = body.lastIndexOf(' Error code: ')
  if (codeIndex !== -1) {
    code = body.slice(codeIndex + ' Error code: '.length)
    body = body.slice(0, codeIndex)
  }

  let prefixSentence: string | null = ''
  const prefix = Object.keys(ERROR_PREFIXES).find((p) => body.startsWith(`${p} `))
  if (prefix) {
    prefixSentence = lookup(ERROR_PREFIXES[prefix]!)
    body = body.slice(prefix.length + 1)
  }

  const reasonSentence = translateReason(body)
  if (prefixSentence === null || !reasonSentence) return null

  const parts = [prefixSentence, reasonSentence]
  if (code) parts.push(lookup(ERROR_CODE_SENTENCE, { code }) ?? '')
  return withSupport(parts.filter(Boolean).join(' '))
}

// --- generic ambire-common messages ---------------------------------------

type CompiledTemplate = { key: string; re: RegExp; names: string[] }

let exactMessages: Set<string> | null = null
let templates: CompiledTemplate[] | null = null

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const compileTemplates = () => {
  exactMessages = new Set()
  templates = []
  // Humanizer warnings are listed by hand: the extract script skips the humanizer folder.
  for (const key of [...CONTROLLER_MESSAGES, ...HUMANIZER_WARNING_MESSAGES]) {
    if (!key.includes('{{')) {
      exactMessages.add(key)
      continue
    }
    const names: string[] = []
    const source = key
      .split(/({{\w+}})/)
      .map((part) => {
        const name = part.match(/^{{(\w+)}}$/)?.[1]
        if (!name) return escapeRegExp(part)
        names.push(name)
        return '([\\s\\S]+?)'
      })
      .join('')
    templates.push({ key, re: new RegExp(`^${source}$`), names })
  }
  // Most literal text first, so "Failed to X for Y" wins over "Failed to {{a}}".
  const literalLength = (key: string) => key.replace(/{{\w+}}/g, '').length
  templates.sort((a, b) => literalLength(b.key) - literalLength(a.key))
}

const translateGeneric = (message: string, depth: number): string | null => {
  if (!exactMessages || !templates) compileTemplates()
  if (exactMessages!.has(message)) return lookup(message)
  for (const { key, re, names } of templates!) {
    const match = message.match(re)
    if (!match) continue
    // Captured values can be English messages themselves ("…: {{message}}").
    const values = Object.fromEntries(
      names.map((name, i) => {
        const value = match[i + 1]!
        return [name, depth < 2 ? translate(value, depth + 1) : value]
      })
    )
    return lookup(key, values)
  }
  return null
}

function translate(message: string, depth: number): string {
  const trimmed = message.trim()
  return translateHumanizedError(trimmed) ?? translateGeneric(trimmed, depth) ?? message
}

const cache = new Map<string, string>()
const CACHE_LIMIT = 500

/**
 * Translates an English message produced by ambire-common for display. Returns
 * the message unchanged when the UI is in English, i18n is not ready, or the
 * message is not a known one. Keep using the original message for logic.
 */
export const translateControllerMessage = <T extends string | null | undefined>(message: T): T => {
  if (!message || typeof message !== 'string') return message
  if (!i18n.isInitialized || !i18n.language || i18n.language === 'en') return message

  const cacheKey = `${i18n.language}\u0000${message}`
  const cached = cache.get(cacheKey)
  if (cached !== undefined) return cached as T

  const out = translate(message, 0)
  if (cache.size >= CACHE_LIMIT) cache.clear()
  cache.set(cacheKey, out)
  return out as T
}

// Translations for a locale load after the first render; drop results that
// were computed with the English fallback.
i18n.on('added', () => cache.clear())
i18n.on('languageChanged', () => cache.clear())

// --- i18nMessage() from ambire-common -------------------------------------
// Where the English text is built with plurals or lists, ambire-common also
// sends an i18nMessage('key', params) next to it (libs/i18n/i18nMessage.ts):
// `banner.i18n.title`, `balanceError.i18n.title`… Those keys are collected into
// I18N_MESSAGE_KEYS by `yarn i18n:extract`.

const formatList = (items: string[]) => {
  try {
    return new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(items)
  } catch {
    return items.join(', ')
  }
}

const translateI18nMessage = ({ key, params }: I18nMessage): string | null => {
  // i18next returns the key itself for a key it doesn't have.
  if (!i18n.exists(key, typeof params?.count === 'number' ? { count: params.count } : undefined))
    return null
  if (!params) return lookup(key)
  const values: Record<string, string | number> = {}
  for (const [name, value] of Object.entries(params)) {
    if (Array.isArray(value)) values[name] = formatList(value)
    // Numbers stay numbers so `count` picks the plural form.
    else values[name] = typeof value === 'string' ? translateControllerMessage(value) : value
  }
  return lookup(key, values)
}

/**
 * Translates a message that ambire-common sends both as English text and as
 * i18nMessage(s). English (and messages without `i18n`) go through
 * translateControllerMessage(); several i18nMessages are sentences joined by a space.
 */
export const translateI18nText = <T extends string | null | undefined>(
  english: T,
  i18nText: I18nText | undefined
): T => {
  if (!english || !i18nText) return translateControllerMessage(english)
  if (!i18n.isInitialized || !i18n.language || i18n.language === 'en') return english
  const parts = (Array.isArray(i18nText) ? i18nText : [i18nText]).map(translateI18nMessage)
  // A key this build doesn't know (submodule newer than en.json): keep English.
  if (parts.some((part) => part === null)) return english
  return parts.join(' ') as T
}
